# Route: /admin/roles/:roleName/edit
# Edit an existing role: description, parent role and permissions. The role name
# is the immutable key (path param), so it is shown read-only.
#
# PUT /api/users/roles/{name} REPLACES the definition — description, inherits,
# permissions — so this form submits all three. The parent is a bound field for
# that reason: a body carrying no `inherits` stores no parent, and the role's
# holders lose every permission they inherited. The select prefills from the
# loaded role, so saving without touching it sends the stored parent back; its
# empty option is how a parent is removed on purpose.
# Permission: Role:update (enforced by PUT /api/users/roles/{name}; the
# detail-page affordance that routes here is gated on the same permission).
#
# Canonical type: Form + FormActions update pattern (same as template-edit).
# The form prefills from the bound role; the picker prefills its rows from the
# role's existing permissions (role-name prop / URL fallback) and injects the
# replacement permissions[] array onto the update action.

page-admin-role-edit:
  type: Page
  archetype: edit
  meta:
    title: $t.admin.roles.edit_title
    auth: required
  params:
    roleName:
      type: string
      required: true
  data:
    role:
      model: RoleDto
      key: $route.roleName
      required: true
    # Options for the parent picker: the assignable catalogue (GET /api/users/roles?assignable=true)
    # — no templates, which the server refuses as a parent, and no TenantAdmin for a tenant user,
    # who gives a role full access by its own permissions instead. The list still holds this role
    # and the roles that inherit from it; the server refuses those too, with a 400 naming why.
    roles:
      model: AssignableRole[]
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - role-edit-header
    - role-edit-form

# ── Header ───────────────────────────────────────────────────────────────────

role-edit-header:
  type: PageHeader
  title: $t.admin.roles.edit_title
  back_route: /admin/roles

# ── Form ─────────────────────────────────────────────────────────────────────

role-edit-form:
  type: Form
  model: RoleDto
  bind:
    content: $data.role
  a11y:
    label: $t.admin.roles.edit_title
  action:
    type: update
    model: RoleDto
    on_success: navigate(/admin/roles)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - role-edit-card
    - role-edit-permissions
    - role-edit-actions

role-edit-card:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - role-edit-name-row
    - role-edit-field-description
    - role-edit-field-parent

# Name is the immutable key — shown read-only.
role-edit-name-row:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.xs
  children:
    - role-edit-name-label
    - role-edit-name-value

role-edit-name-label:
  type: Text
  content: $t.admin.roles.field_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

role-edit-name-value:
  type: Text
  bind:
    content: name
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

role-edit-field-description:
  type: Input
  input_type: textarea
  label: $t.admin.roles.field_description
  bind:
    content: description
  a11y:
    label: $t.admin.roles.field_description_label
  spatial:
    min_height: $size.touch-target

# The parent role this one inherits permissions from. Bound to the loaded role's `inherits`, so
# the update body always carries it. Unqualified binds read the role the Form slices, so the
# page-level `roles` alias is addressed as `$data.`, which reads the page data under any slice. The select's leading entry carries no text
# (a select takes no placeholder), so the helper line says what choosing it does.
role-edit-field-parent:
  type: Input
  input_type: select
  label: $t.admin.roles.field_parent
  helper: $t.admin.roles.field_parent_helper
  bind:
    value: inherits
    options: $data.roles
  a11y:
    label: $t.admin.roles.field_parent_label
  spatial:
    min_height: $size.touch-target

# ── Permission picker (custom widget — interim for xp85 REQ-032; prefilled from the role's existing permissions) ──

role-edit-permissions:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - role-edit-picker

role-edit-picker:
  type: Widget
  ref: xp87-role-permission-picker
  params:
    role-name: $params.roleName

# ── Submit / Cancel ──────────────────────────────────────────────────────────

role-edit-actions:
  type: FormActions
  submit_label: $t.admin.roles.edit_submit
  cancel_route: /admin/roles
